/**
 * shpflt-flow-state — Central user-intent module for the Madreamiga purchase flow.
 *
 * Represents the user's chosen path through the new purchase flow:
 *   - 'pickup'   : user chose "Recogida"  (will hit Zapiet for slot)
 *   - 'shipping' : user chose "Envío"     (will hit Zapiet for slot)
 *   - 'browse'   : user chose "Ver menú"  (free navigation, do NOT auto-open Zapiet)
 *   - null       : no choice yet          (welcome popup should appear)
 *
 * Persistence: sessionStorage. Reset on new browser session by design — a returning
 * user gets the welcome popup again, matching the "público mayor" requirement.
 *
 * Public API (window.shpfltFlowState):
 *   getMode()                     -> 'pickup'|'shipping'|'browse'|null
 *   setMode(mode)                 -> void  (dispatches 'flow:mode-changed')
 *   isBrowseMode()                -> boolean  (sugar for getMode()==='browse')
 *   onModeChange(cb)              -> () => void  (unsubscribe)
 *   requestZapiet(deliveryOption) -> void  (opens Zapiet modal; deliveryOption optional hint)
 *
 * Events (on document, bubbles: true):
 *   flow:mode-changed   detail: { mode, previousMode }
 *   flow:request-zapiet detail: { deliveryOption? }
 */

const STORAGE_KEY = 'shpflt:flow-mode';
const VALID_MODES = new Set(['pickup', 'shipping', 'browse']);

/** @returns {'pickup'|'shipping'|'browse'|null} */
function getMode() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return VALID_MODES.has(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Mirror the current mode onto <html> so CSS can react (e.g. hide Zapiet UI in browse mode). */
function syncRootAttribute(mode) {
  const root = document.documentElement;
  if (!root) return;
  if (mode) {
    root.dataset.shpfltFlowMode = mode;
  } else {
    delete root.dataset.shpfltFlowMode;
  }
}

/** @param {'pickup'|'shipping'|'browse'|null} mode */
function setMode(mode) {
  const previousMode = getMode();
  if (mode === previousMode) {
    syncRootAttribute(mode);
    return;
  }

  try {
    if (mode === null) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else if (VALID_MODES.has(mode)) {
      sessionStorage.setItem(STORAGE_KEY, mode);
    } else {
      return;
    }
  } catch {
    /* sessionStorage may be unavailable in some private modes — silently ignore */
  }

  syncRootAttribute(mode);

  document.dispatchEvent(new CustomEvent('flow:mode-changed', {
    bubbles: true,
    detail: { mode, previousMode },
  }));
}

// On load, mirror whatever mode is already in sessionStorage onto <html>.
syncRootAttribute(getMode());

function isBrowseMode() {
  return getMode() === 'browse';
}

/**
 * Subscribe to mode changes.
 * @param {(detail: {mode: string|null, previousMode: string|null}) => void} callback
 * @returns {() => void} unsubscribe
 */
function onModeChange(callback) {
  const handler = (e) => callback(e.detail);
  document.addEventListener('flow:mode-changed', handler);
  return () => document.removeEventListener('flow:mode-changed', handler);
}

/** @type {'pickup'|'shipping'|null} */
let pendingZapietDeliveryOption = null;

/**
 * Click the Zapiet pickup/delivery tab matching the user's welcome-popup choice.
 *
 * @param {'pickup'|'shipping'} flowOption
 * @returns {boolean} whether the tab was found (and clicked if needed)
 */
function clickZapietDeliveryTab(flowOption) {
  const btnId = flowOption === 'shipping' ? 'delivery' : 'pickup';
  const modal = document.getElementById('ZapietEats-WidgetModal');
  const btn =
    modal?.querySelector(`button#${btnId}[role="tab"]`) ||
    modal?.querySelector(`button#${btnId}`) ||
    document.querySelector(`button#${btnId}[role="tab"]`) ||
    document.getElementById(btnId);

  if (!(btn instanceof HTMLButtonElement)) return false;

  const isSelected =
    btn.getAttribute('aria-selected') === 'true' ||
    btn.classList.contains('ZapietEats-DeliveryOption--active');

  if (!isSelected) btn.click();
  return true;
}

/** Wait for the Zapiet tab buttons to exist, then click the right one. */
function applyPendingZapietDeliveryOption() {
  const flowOption = pendingZapietDeliveryOption;
  if (!flowOption) return;

  if (clickZapietDeliveryTab(flowOption)) {
    pendingZapietDeliveryOption = null;
    return;
  }

  let tries = 0;
  const tick = () => {
    if (!pendingZapietDeliveryOption) return;
    if (clickZapietDeliveryTab(flowOption) || ++tries >= 30) {
      pendingZapietDeliveryOption = null;
      return;
    }
    window.setTimeout(tick, 100);
  };
  window.setTimeout(tick, 100);
}

/**
 * Open the Zapiet Eats modal. If Zapiet has not finished loading yet,
 * waits for `zapieteats:settings:loaded` (with a 5s safety timeout).
 *
 * @param {'pickup'|'shipping'} [deliveryOption]
 */
function requestZapiet(deliveryOption) {
  // Mark this open as user-initiated so the browse-mode guardian below
  // does not immediately close it. Cleared on `zapieteats:modal:closed`.
  userRequestedZapiet = true;

  if (deliveryOption === 'pickup' || deliveryOption === 'shipping') {
    pendingZapietDeliveryOption = deliveryOption;
  }

  document.dispatchEvent(new CustomEvent('flow:request-zapiet', {
    bubbles: true,
    detail: { deliveryOption },
  }));

  const open = () => {
    if (window.ZapietEats && typeof window.ZapietEats.openModal === 'function') {
      window.ZapietEats.openModal();
      document.body.classList.add('zapiet-modal-open');
      applyPendingZapietDeliveryOption();
      return true;
    }
    return false;
  };

  if (open()) return;

  const onReady = () => {
    document.removeEventListener('zapieteats:settings:loaded', onReady);
    clearTimeout(timeoutId);
    open();
  };
  const timeoutId = setTimeout(() => {
    document.removeEventListener('zapieteats:settings:loaded', onReady);
  }, 5000);
  document.addEventListener('zapieteats:settings:loaded', onReady);
}

const flowState = {
  getMode,
  setMode,
  isBrowseMode,
  onModeChange,
  requestZapiet,
};

// Expose globally for legacy non-module code (custom.js, inline snippets).
window.shpfltFlowState = flowState;

// Single delegated listener for any element with data-action="open-zapiet".
// Replaces the inline onclick="ZapietEats.openModal()..." pattern scattered
// across main-menu, hero-banner, password-template, etc.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-action="open-zapiet"]');
  if (!trigger) return;
  e.preventDefault();
  const deliveryOption = trigger.dataset.deliveryOption || undefined;
  requestZapiet(deliveryOption);
});

/**
 * Defensive guardian: if any code (theme, Zapiet app itself, third-party)
 * opens the Zapiet Eats modal while the user is in "browse" mode, close it
 * immediately so the user can keep exploring without interruption.
 *
 * Two complementary signals:
 *  1) Zapiet's own 'zapieteats:modal:opened' event
 *  2) A MutationObserver watching for the WidgetModal element becoming visible
 */
/**
 * Pending browse-mode add-to-cart.
 * When the user is in 'browse' mode and clicks an add-to-cart button, we
 * stash the form here and open Zapiet so they can pick pickup/delivery and
 * a location/slot first. Once they finish that flow (mode flips away from
 * 'browse'), we resubmit the form so the product actually lands in the cart.
 *
 * Rationale: previously we bypassed Zapiet entirely in browse mode and added
 * the product directly. But once a Zapiet location is selected later from
 * the cart, Zapiet's flow forces clearing and rebuilding the cart from
 * scratch — which destroys whatever the user had added. Forcing the location
 * decision *before* the first add eliminates that destructive rebuild.
 *
 * @type {{ form: HTMLFormElement, btn: HTMLButtonElement } | null}
 */
let pendingAdd = null;

/**
 * Set to `true` by `requestZapiet()` so the guardian knows the open was
 * user-initiated (navbar button, hero CTA, password page, etc.) and must
 * not be reverted. Cleared on `zapieteats:modal:closed`.
 * @type {boolean}
 */
let userRequestedZapiet = false;

function closeZapietIfBrowseMode() {
  if (!isBrowseMode()) return;
  // If we intentionally opened Zapiet to collect a slot for a pending add,
  // do NOT auto-close it — the user needs to finish the selection.
  if (pendingAdd) return;
  // If the user explicitly asked to open Zapiet (e.g. via the navbar
  // "Encarga tu pedido" button), respect that and do NOT auto-close.
  if (userRequestedZapiet) return;
  try {
    if (window.ZapietEats && typeof window.ZapietEats.closeModal === 'function') {
      window.ZapietEats.closeModal();
    }
  } catch { /* ignore */ }
  document.body.classList.remove('zapiet-modal-open');

  // Hard fallback: hide the widget DOM if it's still visible
  const modal = document.getElementById('ZapietEats-WidgetModal');
  if (modal) {
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');
  }
}

document.addEventListener('zapieteats:modal:opened', () => {
  applyPendingZapietDeliveryOption();
  closeZapietIfBrowseMode();
});

/**
 * Try to determine which delivery option the user has selected in Zapiet.
 * Combines three signals because Zapiet's event payload isn't reliable
 * across all flows:
 *  1) Event detail object — when the caller passed one in.
 *  2) `window.ZapietEats` runtime properties (selectedDeliveryOption / deliveryOption).
 *  3) Shopify cart attributes Zapiet writes after a successful commit.
 *
 * @param {Object} [detail] - Optional Zapiet event detail.
 * @returns {Promise<'pickup' | 'shipping' | null>}
 */
async function resolveZapietOption(detail) {
  // 1) Event detail
  const fromDetail = detail && (detail.deliveryOption || detail.option || detail.type || detail.value);
  if (fromDetail) {
    const opt = String(fromDetail).toLowerCase();
    if (opt === 'pickup') return 'pickup';
    if (opt === 'delivery' || opt === 'shipping') return 'shipping';
  }

  // 2) Zapiet runtime
  try {
    const z = window.ZapietEats;
    if (z) {
      const live = z.selectedDeliveryOption || z.deliveryOption || z.getSelectedDeliveryOption?.();
      if (live) {
        const opt = String(live).toLowerCase();
        if (opt === 'pickup') return 'pickup';
        if (opt === 'delivery' || opt === 'shipping') return 'shipping';
      }
    }
  } catch { /* ignore */ }

  // 3) sessionStorage — Zapiet Eats persists user choices under keys prefixed
  // with the shop's *.myshopify.com handle (independent of the customer-facing
  // domain). The most explicit signal is `<shop>.searchRequest.deliveryOption`;
  // we also fall back to presence of `selectedLocation` (pickup) /
  // `selectedAddress` (delivery) for older flows.
  try {
    let explicit = null;
    let hasSelectedLocation = false;
    let hasSelectedAddress = false;
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key) continue;
      if (key.endsWith('.searchRequest')) {
        try {
          const obj = JSON.parse(sessionStorage.getItem(key) || 'null');
          const opt = obj?.deliveryOption ? String(obj.deliveryOption).toLowerCase() : null;
          if (opt === 'pickup') { explicit = 'pickup'; break; }
          if (opt === 'delivery' || opt === 'shipping') { explicit = 'shipping'; break; }
        } catch { /* ignore */ }
      } else if (key.endsWith('.selectedLocation')) {
        const v = sessionStorage.getItem(key);
        if (v && v !== 'null' && v !== '""') hasSelectedLocation = true;
      } else if (key.endsWith('.selectedAddress')) {
        const v = sessionStorage.getItem(key);
        if (v && v !== 'null' && v !== '""') hasSelectedAddress = true;
      }
    }
    if (explicit) return explicit;
    if (hasSelectedLocation) return 'pickup';
    if (hasSelectedAddress) return 'shipping';
  } catch { /* ignore */ }

  // 4) Cart attributes — kept as last fallback for theme/app configurations
  // that surface the commit through `/cart.js` instead of localStorage.
  try {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (r.ok) {
      const cart = await r.json();
      const attrs = cart.attributes || {};
      const keys = Object.keys(attrs).map(k => k.toLowerCase());
      const has = (sub) => keys.some(k => k.includes(sub));
      if (has('pickup-location') || has('pickup-date') || has('pickup_time')) return 'pickup';
      if (has('delivery-address') || has('delivery-date') || has('shipping-address')) return 'shipping';
    }
  } catch { /* ignore */ }

  return null;
}

// When the user picks a delivery option inside Zapiet, flip the flow mode
// away from 'browse' so subsequent navigation no longer treats the session
// as browse-only.
document.addEventListener('zapieteats:deliveryoption:changed', async (e) => {
  const option = await resolveZapietOption(e?.detail);
  if (option) setMode(option);
});

// Also commit on these signals — Zapiet doesn't always fire
// `deliveryoption:changed` cleanly on completion.
for (const evt of ['zapieteats:restaurant:changed', 'zapieteats:settings:loaded']) {
  document.addEventListener(evt, async () => {
    if (!isBrowseMode()) return;
    const option = await resolveZapietOption();
    if (option) setMode(option);
  });
}

// Mirror the open/close state on <body> so legacy CSS keyed on
// `.zapiet-modal-open` is in sync. The class is added in `requestZapiet()`
// when we open Zapiet, but Zapiet's own close paths (X button, ESC,
// backdrop click) don't run our code, so the class would otherwise stick.
//
// Additionally: if there is a pending browse-mode add and Zapiet has now
// recorded a delivery commitment (event payload, runtime, or cart attrs),
// flip mode and resubmit the stashed form.
document.addEventListener('zapieteats:modal:closed', async () => {
  document.body.classList.remove('zapiet-modal-open');
  userRequestedZapiet = false;
  pendingZapietDeliveryOption = null;

  if (!pendingAdd) return;
  const { form, btn } = pendingAdd;

  // Re-resolve in case the runtime needed a beat to settle.
  if (isBrowseMode()) {
    const option = await resolveZapietOption();
    if (option) setMode(option);
  }

  if (!isBrowseMode()) {
    pendingAdd = null;
    setTimeout(() => {
      if (form.isConnected) form.requestSubmit(btn);
    }, 50);
  } else {
    // User closed Zapiet without committing — abandon the pending add.
    pendingAdd = null;
  }
});

/**
 * Browse-mode add-to-cart interception.
 *
 * In browse mode the user hasn't picked pickup/delivery yet. Adding the
 * product before that decision means Zapiet will later force a destructive
 * cart rebuild when the location is finally chosen. Instead, intercept the
 * click here, stash the form, and route the user through Zapiet first.
 * Once Zapiet's modal closes with a valid selection, the modal:closed
 * listener above resubmits the form for them.
 *
 * Capture phase + stopImmediatePropagation prevents Zapiet's own at-target
 * click handler from running its default branch — we call requestZapiet()
 * ourselves so the open is observable and `pendingAdd` is set first.
 *
 * Selector matches both collection-card buttons and PDP buttons that live
 * inside an `<ajax-cart-product-form>` wrapper.
 */
document.addEventListener('click', async (e) => {
  if (!isBrowseMode()) return;
  const btn = e.target.closest?.('ajax-cart-product-form button[type="submit"]');
  if (!btn || btn.disabled) return;
  const form = btn.closest('form');
  if (!form) return;
  e.stopImmediatePropagation();
  e.preventDefault();

  // Fast path: if Zapiet already has a committed selection in sessionStorage
  // (from an earlier session within this tab), flip the mode and submit the
  // form directly — no need to reopen Zapiet's modal.
  const existing = await resolveZapietOption();
  if (existing) {
    setMode(existing);
    setTimeout(() => {
      if (form.isConnected) form.requestSubmit(btn);
    }, 0);
    return;
  }

  pendingAdd = { form, btn };
  requestZapiet();
}, true);

// On load, if the user is marked 'browse' but Zapiet already has a committed
// selection (from a previous tab/session that was carried over), correct the
// mode so the next add-to-cart doesn't pointlessly reopen the slot picker.
(async () => {
  if (!isBrowseMode()) return;
  const existing = await resolveZapietOption();
  if (existing) setMode(existing);
})();

if (typeof MutationObserver !== 'undefined') {
  const guardianObserver = new MutationObserver(() => {
    if (!isBrowseMode()) return;
    if (pendingAdd) return; // intentionally open for the slot picker
    const modal = document.getElementById('ZapietEats-WidgetModal');
    if (modal && modal.offsetParent !== null) {
      closeZapietIfBrowseMode();
    }
  });
  // Start observing once the DOM body exists
  const startGuardian = () => {
    if (document.body) {
      guardianObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'aria-hidden'] });
    }
  };
  if (document.body) startGuardian();
  else document.addEventListener('DOMContentLoaded', startGuardian, { once: true });
}

export default flowState;
export { getMode, setMode, isBrowseMode, onModeChange, requestZapiet };
